<script setup lang="ts">
    import { Column, DataTable, PageState } from 'primevue';
    import { __ } from '@/js/stores/expressions';
    import CoachingTableCell from './CoachingTableCell.vue';
    import TablePaginator from '../../dataTable/TablePaginator.vue';
    import { useNumericQueryParam, useQueryString } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import { useGetCoachingForms } from '@/js/services/api/coachingForm/coachingFormQueries';
    import SortableColumnHeader from '../../dataTable/SortableColumnHeader.vue';
    import {
        CoachingFormTableColumn,
        useCoachingFormTableData,
    } from '@/js/stores/coaching-form-table';
    import { computed, ref, watch } from 'vue';
    import TableLoadingOverlay from '../../dataTable/TableLoadingOverlay.vue';
    import BottomScrollFade from '../../dataTable/BottomScrollFade.vue';

    defineEmits<{
        (e: 'show-version-history', value: string): void;
    }>();

    const tableRef = ref<HTMLElement | null>(null);

    const columns: CoachingFormTableColumn[] = [
        { field: 'title', name: __('coaching-form-table-column-title') },
        { field: 'useAi', name: __('coaching-form-table-column-category') },
        { field: 'createdAt', name: __('coaching-form-table-column-created-at'), sortable: true },
        { field: 'createdBy', name: __('coaching-form-table-column-created-by') },
        { field: 'updatedBy', name: __('coaching-form-table-column-updated-by') },
        { field: 'actions', name: __('coaching-form-table-column-actions') },
    ];

    const querystring = useQueryString();

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('coaching-forms');

    const coachingFormTableData = useCoachingFormTableData();

    const includeInactive = computed(() => coachingFormTableData.showInactive === 'true');

    const { data, isFetching } = useGetCoachingForms(includeInactive);

    watch(
        () => data.value,
        (newVal) => {
            if (newVal) {
                coachingFormTableData.setForms(newVal);
            }
        },
        { immediate: true }
    );

    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };
</script>
<template>
    <div ref="tableRef" class="echo-table-wrapper echo-table-wrapper--fill">
        <DataTable
            class="p-datatable-no-top-border row-padding"
            :value="coachingFormTableData.formData"
            data-key="id"
            paginator
            :total-records="data?.forms.length ?? 0"
            :loading="isFetching"
            :rows="pageSize"
            :page="page"
            :first="page * pageSize"
            :scrollable="true"
            scroll-height="flex"
        >
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <template #empty>
                <div class="h-100 flex items-center justify-center">
                    <h2 v-if="!isFetching" class="text-center">
                        {{ __('coaching-form-table-empty-message') }}
                    </h2>
                </div>
            </template>
            <Column v-for="col in columns" :key="col.field" :field="col.field">
                <template #header>
                    <SortableColumnHeader
                        :name="col.name"
                        :sortable="col.sortable"
                        @sort="(dir) => coachingFormTableData.changeFilter('sortDirection', dir)"
                    />
                </template>
                <template #body="{ data: rowData }">
                    <CoachingTableCell
                        :data="rowData"
                        :column="col.field"
                        @show-version-history="$emit('show-version-history', $event)"
                    />
                </template>
            </Column>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <BottomScrollFade :container-ref="tableRef" />
    </div>
</template>
